import { Link } from "react-router-dom";
import { Icon } from "@/components/Icon";
import { InflowLogo } from "@/components/InflowLogo";
import { tg } from "@/lib/telegram";

/**
 * Уніфікована верхня pill-навігація. Три режими:
 *
 *  1. <BackNav />                       → тільки «Меню» (повертає на головну)
 *  2. <BackNav onBack={fn} />           → тільки «Назад» (in-app navigation)
 *  3. <BackNav onBack={fn} showMenu />  → ОБИДВІ: «Назад» зліва, «Меню» справа
 *
 * Правило:
 *   • Top-level сторінка з головної (Categories, Profile, etc.) → варіант 1
 *   • Внутрішня сторінка (lesson detail, sub-view)              → варіант 3
 *   • In-app модал/sub-view який повертає в межах однієї сторінки → варіант 2
 */
export function BackNav({
  to = "/",
  onBack,
  showMenu = false,
  backLabel = "Назад",
  menuLabel = "Меню",
  closeApp = false,
  closeLabel = "Закрити",
}: {
  to?: string;
  onBack?: () => void;
  showMenu?: boolean;
  backLabel?: string;
  menuLabel?: string;
  /** Юзер 2026-05-23: «на сторінці конкретної вакансії — кнопка
   * Закрити що закриває Mini App в цілому». Якщо true — замість
   * Link на головну рендериться button з Telegram.WebApp.close(). */
  closeApp?: boolean;
  closeLabel?: string;
}) {
  // Канонічний стиль усіх кутових кнопок (Назад/Меню/Профіль/Підтримка) —
  // бежевий 3D pill, висота h-8 (юзер 2026-06-09: «зроби всюди однаково»).
  // whitespace-nowrap + shrink-0: лейбл «База знань» переносився у два
  // рядки на вузьких екранах (юзер 2026-06-14). Тепер pill не стискається
  // і текст завжди в один рядок.
  const pillCls =
    "flex h-8 shrink-0 items-center gap-1.5 whitespace-nowrap rounded-full bg-surface-soft px-3 font-display text-[0.75rem] font-semibold text-ink shadow-card-soft active:scale-95 transition";

  const backArrow = (
    <Icon
      name="arrow-right"
      size={14}
      className="rotate-180"
      color="#FE6B04"
    />
  );
  const homeIcon = <Icon name="home" size={14} color="#FE6B04" />;
  const closeIcon = (
    <Icon name="plus" size={14} color="#FE6B04" className="rotate-45" />
  );

  // Праве крило — контроли (back / menu / close) залежно від варіанту.
  // Логотип ЗАВЖДИ зліва (юзер 2026-06-09: «хедер з логотипом в одному
  // місці на кожній сторінці, як на головній»).
  let controls: React.ReactNode;
  if (onBack && showMenu) {
    // Назад (callback) + Меню
    controls = (
      <>
        <button type="button" onClick={onBack} className={pillCls}>
          {backArrow}
          {backLabel}
        </button>
        <Link to="/" className={pillCls}>
          {menuLabel}
          {homeIcon}
        </Link>
      </>
    );
  } else if (showMenu && to !== "/") {
    // Назад (to=URL) + Меню
    controls = (
      <>
        <Link to={to} className={pillCls}>
          {backArrow}
          {backLabel}
        </Link>
        <Link to="/" className={pillCls}>
          {menuLabel}
          {homeIcon}
        </Link>
      </>
    );
  } else if (onBack) {
    // Тільки Назад (in-app callback)
    controls = (
      <button type="button" onClick={onBack} className={pillCls}>
        {backArrow}
        {backLabel}
      </button>
    );
  } else if (closeApp) {
    // «Закрити» — закриває Mini App
    controls = (
      <button type="button" onClick={() => tg()?.close?.()} className={pillCls}>
        {closeIcon}
        {closeLabel}
      </button>
    );
  } else {
    // Тільки Меню (default — top-level сторінка). ЗАВЖДИ домик справа —
    // єдиний вигляд кнопки «Меню» на всіх сторінках (юзер 2026-06-11:
    // «меню завжди з домиком, в одному місці; назад — зі стрілкою»).
    controls = (
      <Link to={to} className={pillCls}>
        {menuLabel}
        {homeIcon}
      </Link>
    );
  }

  return (
    <div className="mb-4 flex items-center justify-between gap-2">
      <InflowLogo size={18} to="/" />
      <div className="flex items-center gap-2">{controls}</div>
    </div>
  );
}
